<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { useDisplay } from 'vuetify';
import { useRoute, useRouter } from 'vue-router';
import { useTelehealthSessionStore } from '@/stores/telehealthSession';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';
import BnContactSearch from '@/components/BnContactSearch.vue';
import BnTelehealthWhiteboard from '@/components/telehealth/BnTelehealthWhiteboard.vue';

const session = useTelehealthSessionStore();
const authStore = useAuthStore();
const notify = useNotificationStore();
const router = useRouter();
const route = useRoute();
const { mobile } = useDisplay();

// Flag the body only while a session is open (this component is always mounted, so
// the flag must track session.open, not mount). The global rule below keys off the
// flag to hide the Usersnap button; it comes off when the session ends. Nothing persists.
watch(
    () => session.open,
    (open) => document.body.classList.toggle('bn-telehealth-active', open),
    { immediate: true },
);

onBeforeUnmount(() => {
    document.body.classList.remove('bn-telehealth-active');
    document.body.style.removeProperty('--bn-dock-width');
    window.removeEventListener('resize', onWindowResize);
});

// Attach a Daily MediaStreamTrack to a <video>/<audio> element (call-object mode
// renders its own media). Re-attaches on track change; clears when null.
const vDailyTrack = {
    mounted: (el, binding) => attach(el, binding.value),
    updated: (el, binding) => attach(el, binding.value),
    beforeUnmount: (el) => {
        el.srcObject = null;
    },
};
function attach(el, track) {
    const current = el.srcObject?.getTracks?.()[0] || null;
    if (track === current) return;
    el.srcObject = track ? new MediaStream([track]) : null;
}

const presenceLabel = (p) => ({ not_arrived: 'Not arrived', waiting: 'Waiting', admitted: 'Admitted', in_session: 'In session', left: 'Left' })[p] || p;
const presenceColor = (p) => ({ not_arrived: 'grey', waiting: 'orange', admitted: 'blue', in_session: 'green', left: 'grey' })[p] || 'grey';

const collapsed = computed(() => session.dockState === 'collapsed');
const docked = computed(() => session.dockState === 'docked');
const fullscreen = computed(() => session.dockState === 'fullscreen');
const inSessionCount = computed(() => session.roster.filter((r) => r.inSession).length + (session.joined ? 1 : 0));
const waitingCount = computed(() => session.roster.filter((r) => r.presence === 'waiting').length);

// Width fed to Vuetify's layout, which reserves a column of exactly this many px and
// reflows v-main by it (Vuetify sizes off the PROP, not the rendered element). The
// collapsed rail reserves its 72px column so content makes room for it. Docked and
// full-screen reserve 0 so the panel floats OVER content instead of pushing it: the
// docked panel's real width is the resizable `panelWidth` below, applied as an
// inline style, and full-screen is a fixed overlay. Mobile keeps real widths (it's a temporary drawer
// that already overlays with a scrim). Permanent↔temporary never flips at runtime
// (flipping it made the drawer vanish); only the reserved width changes.
// --- Panel width ------------------------------------------------------------
// The clinician documents beside the call, and how much room each deserves is
// theirs to decide, so the panel is draggable and the choice is remembered.
const RAIL_W = 72;
const MIN_W = 300;
const DEFAULT_W = 380;
/**
 * Versioned: the ceiling used to be a flat 720px, so widths saved while that was
 * in force reopen far wider than the panel was ever meant to start. Bumping the
 * key retires those and puts everyone back on the default; the width someone
 * sets from here on is still remembered.
 */
const WIDTH_KEY = 'bn.telehealth.dockWidth.v2';

/**
 * The panel may be dragged out to half the window. Relative rather than a fixed
 * ceiling because "half" is what people actually want — a flat cap reads as
 * roomy on a laptop and cramped on a wide monitor. The floor keeps it usable if
 * the window is genuinely tiny.
 */
const maxPanelWidth = () => Math.max(MIN_W + 120, Math.round(window.innerWidth / 2));
const clampWidth = (px) => Math.min(maxPanelWidth(), Math.max(MIN_W, Math.round(px)));

const readStoredWidth = () => {
    try {
        const stored = Number(localStorage.getItem(WIDTH_KEY));
        return Number.isFinite(stored) && stored >= MIN_W ? clampWidth(stored) : DEFAULT_W;
    } catch {
        // Private windows and blocked site data both throw; the default is fine.
        return DEFAULT_W;
    }
};
const panelWidth = ref(readStoredWidth());

const setWidth = (px) => {
    panelWidth.value = clampWidth(px);
    try {
        localStorage.setItem(WIDTH_KEY, String(panelWidth.value));
    } catch {
        // Not worth surfacing — the width just won't survive a reload.
    }
};

/** Drag the panel's leading edge. Pointer capture keeps it tracking off-element. */
const resizing = ref(false);
const onResizeStart = (event) => {
    resizing.value = true;
    event.currentTarget?.setPointerCapture?.(event.pointerId);
};
const onResizeMove = (event) => {
    if (!resizing.value) return;
    // The panel is right-anchored, so its width is whatever remains to the edge.
    setWidth(window.innerWidth - event.clientX);
};
const onResizeEnd = (event) => {
    resizing.value = false;
    try {
        event.currentTarget?.releasePointerCapture?.(event.pointerId);
    } catch {
        // Also bound to pointercancel, where the pointer is already gone and
        // releasing it throws NotFoundError. Nothing to release is fine.
    }
};
/** Keyboard equivalent, so the handle is not mouse-only. */
const nudgeWidth = (delta) => setWidth(panelWidth.value + delta);

// The ceiling moves with the window, so a panel dragged wide on a large screen
// has to come back in when the window shrinks rather than swallowing it.
const onWindowResize = () => {
    if (panelWidth.value > maxPanelWidth()) panelWidth.value = clampWidth(panelWidth.value);
};
window.addEventListener('resize', onWindowResize);

const dockWidth = computed(() => {
    if (mobile.value) return collapsed.value ? RAIL_W : panelWidth.value;
    return collapsed.value ? RAIL_W : 0;
});

// --- Sharing (#5040) -------------------------------------------------------

const whiteboardOpen = ref(false);
const whiteboard = ref(null);

/**
 * Some sites refuse to be embedded (X-Frame-Options / frame-ancestors) and there
 * is no reliable way to detect that from the parent, so the surface always offers
 * opening the page in a tab alongside the frame rather than pretending it worked.
 */
// Only allowlisted hosts may be framed; everything else opens on the viewer's
// own device. Even an allowlisted one keeps the escape hatch, since a site can
// still refuse framing and we cannot detect that from here.

/**
 * The board fills the window except the strip the dock occupies. Vuetify reserves
 * no column for the docked panel (it floats), so the offset is taken from the
 * dock's own visual width rather than from the layout.
 */
/**
 * A definite height for the board's canvas. It cannot be `100%`: the whiteboard's
 * own root is auto-height, so a percentage resolves against nothing and the
 * surface collapses to zero — which silently hides the text caret (the surface
 * clips overflow) and leaves nothing to draw on. Derived from the panel's fixed
 * geometry: the sharing banner (48) + the board's header (44) + its padding (24).
 */
const boardSurfaceHeight = 'calc(100vh - 116px)';

const boardRight = computed(() => {
    if (mobile.value || fullscreen.value) return 0;
    return collapsed.value ? RAIL_W : panelWidth.value;
});

/**
 * Publish the dock's width so full-screen surfaces elsewhere — the form filler
 * dialog above all — can stop at its edge instead of covering the call. A CSS
 * variable rather than a prop because those surfaces are rendered by pages that
 * know nothing about telehealth.
 */
watch(
    () => [session.open, boardRight.value],
    ([open, width]) => document.body.style.setProperty('--bn-dock-width', open ? `${width}px` : '0px'),
    { immediate: true },
);

/**
 * The clinician sees and fills a shared form in the real form filler, not here
 * — this routes to it. The dock keeps the share controls; the filler carries
 * its own sharing strip once open.
 */
const toFiller = (taskId) => taskId && router.push({ name: route.name, params: { ...route.params, activeDialog: 'form', activeId: taskId } });

/** The form this clinician should land on; the filler switches between the rest. */
const hostForm = computed(() => session.formForHost(authStore.practitioner?.id));

/**
 * The share menu no longer lists every form: the filler already has a form
 * switcher, and sharing lives on its own bar. This just gets the clinician
 * there — nothing is shared until they say so.
 */
/**
 * Open the clinician's form filler on the form they should be writing.
 *
 * Starting the session begins the appointment, which is what creates the
 * encounter and its forms — so by the time this panel opens the list is
 * accurate. Nothing to retry: no form here means no form on the appointment.
 */
const openFormFiller = () => {
    session.shareMenuOpen = false;
    toFiller(hostForm.value?.taskId);
};

const shareWhiteboard = async () => {
    await session.shareWhiteboard();
    whiteboardOpen.value = true;
};

// The surface opens itself when a share appears — from either side — and can be
// dismissed without stopping the share, matching how the board behaves.
const screenSurfaceOpen = ref(true);
watch(
    () => !!session.screenShare,
    (sharing) => {
        if (sharing) screenSurfaceOpen.value = true;
    },
);

// A refused or failed share is surfaced rather than silently doing nothing.
watch(
    () => session.screenShareError,
    (message) => {
        if (message) notify.add({ message, color: 'warning' });
    },
);

const stopSharing = async () => {
    await session.unshare();
    whiteboardOpen.value = false;
};

/**
 * Whiteboard traffic rides Daily's peer data channel (see the store). The host
 * both answers a late joiner's request for the current board and applies the
 * strokes they draw.
 */
watch(
    () => session.appMessage,
    (envelope) => {
        const message = envelope?.data;
        if (!message || typeof message !== 'object') return;
        if (message.t === 'wb') whiteboard.value?.applyRemote(message.stroke);
        else if (message.t === 'wb-clear') whiteboard.value?.applyClear();
        // A late joiner asking what's on the board: replay it from memory. It
        // exists nowhere else — that is what ephemeral means here.
        //
        // Only answer if the board is actually mounted. The reply broadcasts to
        // everyone, so replying "[]" because we have no board would clear the
        // drawing on every participant already looking at one.
        else if (message.t === 'wb-req' && whiteboard.value) {
            session.sendAppMessage({ t: 'wb-sync', strokes: whiteboard.value.snapshot() });
        }
    },
);

// Sharing ends with the session; nothing about the board outlives it.
watch(
    () => session.isSharing,
    (sharing) => {
        if (!sharing) {
            whiteboardOpen.value = false;
        }
    },
);
</script>

<template>
    <!--
        Full screen has to escape IonPage.

        The dock lives inside <IonPage>, which establishes a containing block, so
        a `position: fixed` child is trapped inside it and its z-index only
        competes with IonPage's siblings — no matter how large. Vuetify teleports
        the form filler's dialog out to its own overlay container, so the dialog
        always won and the "full screen" dock disappeared behind it.

        Teleporting only while full screen keeps everything else as it was: the
        collapsed rail still reserves its column through Vuetify's layout, which
        only works while the drawer sits in the layout's DOM. The drawer node is
        moved rather than remounted, so the video elements keep their attached
        MediaStreams and carry on playing.
    -->
    <Teleport to="body" :disabled="!fullscreen">
        <v-navigation-drawer
            v-if="session.open"
            :model-value="true"
            location="right"
            :absolute="!mobile"
            :permanent="!mobile"
            :temporary="mobile"
            :width="dockWidth"
            color="#1e1e1e"
            theme="dark"
            class="bn-th-dock"
            :class="{ 'bn-th-dock--collapsed': collapsed, 'bn-th-dock--docked': docked, 'bn-th-dock--full': fullscreen, 'bn-th-dock--resizing': resizing }"
            :style="docked && !mobile ? { width: `${panelWidth}px`, maxWidth: `${panelWidth}px` } : undefined"
            data-cy="thDock"
        >
            <!-- Collapsed rail: avatars + core controls -->
            <template v-if="collapsed">
                <v-btn icon size="small" variant="text" class="bn-th-rail-btn" data-cy="thExpandRail" @click="session.setDockState('docked')">
                    <font-awesome-icon :icon="['far', 'chevron-left']" />
                </v-btn>
                <div class="bn-th-rail-avatars">
                    <v-avatar v-for="t in session.tiles" :key="t.key" size="34" color="primary" class="mb-2">
                        <span class="text-caption">{{ (t.name || '?').charAt(0) }}</span>
                    </v-avatar>
                </div>
                <div class="bn-th-rail-controls">
                    <v-btn icon size="small" :color="session.localAudio ? 'grey-darken-2' : 'error'" data-cy="thRailMic" @click="session.toggleMic()">
                        <font-awesome-icon :icon="['far', session.localAudio ? 'microphone' : 'microphone-slash']" />
                    </v-btn>
                    <v-btn icon size="small" color="error" class="mt-2" data-cy="thRailEnd" @click="session.endSession()">
                        <font-awesome-icon :icon="['far', 'phone-slash']" />
                    </v-btn>
                </div>
            </template>

            <!-- Expanded panel -->
            <template v-else>
                <!-- Drag the leading edge to rebalance the call against the chart.
                     Arrow keys do the same, so it is not mouse-only. -->
                <button
                    v-if="!mobile && docked"
                    type="button"
                    class="bn-th-resize"
                    title="Drag to resize — or use the arrow keys"
                    aria-label="Resize the telehealth panel"
                    data-cy="thResize"
                    @pointerdown.prevent="onResizeStart"
                    @pointermove="onResizeMove"
                    @pointerup="onResizeEnd"
                    @pointercancel="onResizeEnd"
                    @keydown.left.prevent="nudgeWidth(24)"
                    @keydown.right.prevent="nudgeWidth(-24)"
                >
                    <span class="bn-th-resize-grip"></span>
                </button>

                <div class="bn-th-header d-flex align-center px-3">
                    <font-awesome-icon :icon="['far', 'video']" class="mr-2" />
                    <span class="text-subtitle-2">Telehealth session</span>
                    <v-spacer />
                    <v-btn icon size="x-small" variant="text" :title="fullscreen ? 'Exit full screen' : 'Full screen'" data-cy="thExpand" @click="session.expand()">
                        <font-awesome-icon :icon="['far', fullscreen ? 'compress-alt' : 'expand-alt']" />
                    </v-btn>
                    <v-btn icon size="x-small" variant="text" title="Collapse" data-cy="thCollapse" @click="session.collapse()">
                        <font-awesome-icon :icon="['far', 'chevron-right']" />
                    </v-btn>
                </div>

                <div v-if="session.loading" class="d-flex justify-center align-center flex-grow-1">
                    <v-progress-circular indeterminate color="primary" />
                </div>
                <div v-else-if="session.errorMsg" class="d-flex justify-center align-center flex-grow-1 pa-6 text-center text-error" data-cy="thDockError">
                    {{ session.errorMsg }}
                </div>

                <template v-else>
                    <!-- Video tiles -->
                    <div class="bn-th-video flex-grow-1">
                        <div v-if="session.tiles.length" class="bn-th-grid">
                            <div v-for="t in session.tiles" :key="t.key" class="bn-th-tile" :data-cy="t.local ? 'thTileLocal' : 'thTile'">
                                <video v-daily-track="t.videoTrack" autoplay playsinline :muted="t.local" class="bn-th-video-el" :class="{ 'bn-th-video-off': !t.videoOn }"></video>
                                <div v-if="!t.videoOn" class="bn-th-video-placeholder">
                                    <v-avatar size="56" color="primary"
                                        ><span class="text-h6">{{ (t.name || '?').charAt(0) }}</span></v-avatar
                                    >
                                </div>
                                <div class="bn-th-tile-label"><font-awesome-icon v-if="!t.audioOn" :icon="['far', 'microphone-slash']" class="mr-1" size="xs" />{{ t.name }}</div>
                            </div>
                        </div>
                        <div v-else class="bn-th-empty">Waiting for participants to join…</div>
                        <!-- Remote audio (hidden) -->
                        <audio v-for="t in session.tiles.filter((x) => !x.local)" :key="'a-' + t.key" v-daily-track="t.audioTrack" autoplay></audio>
                    </div>

                    <!-- Share menu: what the host can put in front of everyone.
                         Adding a form to the encounter happens elsewhere; this only
                         offers what is already there. -->
                    <div v-if="session.shareMenuOpen" class="bn-th-roster" data-cy="thSharePanel">
                        <div class="d-flex align-center px-3 py-1">
                            <span class="text-caption font-weight-medium text-uppercase">Share with everyone</span>
                            <v-spacer />
                            <v-btn icon size="x-small" variant="text" title="Close" data-cy="thShareClose" @click="session.shareMenuOpen = false">
                                <font-awesome-icon :icon="['far', 'xmark']" />
                            </v-btn>
                        </div>
                        <v-list density="compact" class="py-0">
                            <v-list-item class="px-3" :disabled="session.formsLoading || !hostForm" data-cy="thShareForm" @click="openFormFiller">
                                <template #prepend><font-awesome-icon :icon="['far', 'file-lines']" class="mr-3" /></template>
                                <v-list-item-title class="text-body-2">Share a form</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">
                                    <template v-if="session.formsLoading">Looking for forms…</template>
                                    <template v-else-if="hostForm">Opens {{ hostForm.title }} — share it from there</template>
                                    <template v-else-if="session.hasEncounter">No forms on this appointment</template>
                                    <template v-else>This appointment has no encounter yet</template>
                                </v-list-item-subtitle>
                            </v-list-item>

                            <v-divider class="my-1" />

                            <v-list-item class="px-3" data-cy="thShareWhiteboard" @click="shareWhiteboard">
                                <template #prepend><font-awesome-icon :icon="['far', 'chalkboard']" class="mr-3" /></template>
                                <v-list-item-title class="text-body-2">Whiteboard</v-list-item-title>
                                <v-list-item-subtitle class="text-caption">Not saved</v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>

                    <!-- Roster -->
                    <div v-else class="bn-th-roster">
                        <div class="d-flex align-center px-3 py-1">
                            <span class="text-caption font-weight-medium">In the session · {{ inSessionCount }}</span>
                            <v-spacer />
                            <v-btn v-if="waitingCount > 1" size="x-small" variant="tonal" color="primary" class="mr-1" data-cy="thAdmitAll" @click="session.admitAll()">
                                Admit all ({{ waitingCount }})
                            </v-btn>
                            <v-btn size="x-small" variant="text" color="primary" data-cy="thAdd" @click="session.openAdd()"> <font-awesome-icon :icon="['far', 'user-plus']" class="mr-1" />Add </v-btn>
                        </div>
                        <v-list density="compact" class="py-0">
                            <v-list-item v-for="r in session.roster" :key="r.id" :title="r.displayName || 'Participant'" class="px-3">
                                <template #subtitle>
                                    <v-chip :color="r.inSession ? 'green' : presenceColor(r.presence)">
                                        {{ r.inSession ? 'In session' : presenceLabel(r.presence) }}
                                    </v-chip>
                                </template>
                                <template #append>
                                    <!-- Waiting → admit -->
                                    <v-btn v-if="r.presence === 'waiting'" size="small" color="primary" data-cy="thAdmit" @click="session.admit(r.id)">Admit</v-btn>
                                    <!-- Not arrived → invite -->
                                    <template v-else-if="r.presence === 'not_arrived' && r.channels">
                                        <template v-if="r.channels.sms || r.channels.email">
                                            <v-btn
                                                v-if="r.channels.sms"
                                                size="small"
                                                variant="tonal"
                                                color="primary"
                                                class="mr-1"
                                                :loading="session.invitingId === r.id"
                                                data-cy="thInviteSms"
                                                @click="session.invite(r.id, 'sms')"
                                            >
                                                <font-awesome-icon :icon="['far', 'comment-sms']" />
                                            </v-btn>
                                            <v-btn
                                                v-if="r.channels.email"
                                                size="small"
                                                variant="tonal"
                                                color="primary"
                                                :loading="session.invitingId === r.id"
                                                data-cy="thInviteEmail"
                                                @click="session.invite(r.id, 'email')"
                                            >
                                                <font-awesome-icon :icon="['far', 'envelope']" />
                                            </v-btn>
                                        </template>
                                        <span v-else class="text-caption text-medium-emphasis">No contact info</span>
                                    </template>
                                    <!-- Admitted → host controls. Hold is offered as
                                         soon as they are admitted, not only once their
                                         video has connected: a clinician wanting a
                                         private word should not have to wait for
                                         someone's camera to come up first. -->
                                    <template v-else-if="r.presence === 'admitted' || r.presence === 'in_session'">
                                        <v-btn v-if="r.inSession" icon size="small" variant="text" :title="'Mute ' + r.displayName" data-cy="thMute" @click="session.muteParticipant(r.sessionId)">
                                            <font-awesome-icon :icon="['far', 'microphone-slash']" />
                                        </v-btn>
                                        <v-btn icon size="small" variant="text" :title="'Move ' + r.displayName + ' to the waiting room'" data-cy="thHold" @click="session.hold(r.id, r.sessionId)">
                                            <font-awesome-icon :icon="['far', 'circle-pause']" />
                                        </v-btn>
                                        <v-btn
                                            v-if="r.inSession"
                                            icon
                                            size="small"
                                            variant="text"
                                            color="error"
                                            :title="'Remove ' + r.displayName"
                                            data-cy="thRemove"
                                            @click="session.removeParticipant(r.sessionId)"
                                        >
                                            <font-awesome-icon :icon="['far', 'user-xmark']" />
                                        </v-btn>
                                    </template>
                                </template>
                            </v-list-item>
                        </v-list>
                    </div>

                    <!-- Control bar -->
                    <div class="bn-th-controls d-flex align-center justify-center ga-2 py-2">
                        <v-btn icon size="small" :color="session.localAudio ? 'grey-darken-3' : 'error'" data-cy="thMic" :title="session.localAudio ? 'Mute' : 'Unmute'" @click="session.toggleMic()">
                            <font-awesome-icon :icon="['far', session.localAudio ? 'microphone' : 'microphone-slash']" />
                        </v-btn>
                        <v-btn
                            icon
                            size="small"
                            :color="session.localVideo ? 'grey-darken-3' : 'error'"
                            data-cy="thCam"
                            :title="session.localVideo ? 'Turn camera off' : 'Turn camera on'"
                            @click="session.toggleCamera()"
                        >
                            <font-awesome-icon :icon="['far', session.localVideo ? 'video' : 'video-slash']" />
                        </v-btn>
                        <v-btn
                            v-if="session.meeting?.mode !== 'audio'"
                            icon
                            size="small"
                            :color="session.screenSharing ? 'primary' : 'grey-darken-3'"
                            :title="session.screenSharing ? 'Stop sharing your screen' : 'Share your screen'"
                            data-cy="thScreenShare"
                            @click="session.toggleScreenShare()"
                        >
                            <font-awesome-icon :icon="['far', session.screenSharing ? 'display-slash' : 'display']" />
                        </v-btn>
                        <v-btn
                            icon
                            size="small"
                            :color="session.isSharing || session.shareMenuOpen ? 'primary' : 'grey-darken-3'"
                            title="Share with everyone"
                            data-cy="thShare"
                            @click="session.shareMenuOpen ? (session.shareMenuOpen = false) : session.openShareMenu()"
                        >
                            <font-awesome-icon :icon="['far', 'share-nodes']" />
                        </v-btn>
                        <v-btn color="error" size="small" variant="flat" data-cy="thEnd" @click="session.endSession()"> <font-awesome-icon :icon="['far', 'phone-slash']" class="mr-1" />End </v-btn>
                    </div>
                </template>
            </template>
        </v-navigation-drawer>
    </Teleport>

    <!-- Sharing banner. Teleported to the body as a fixed bar so it reads across
         the whole app, not just the dock: whatever the clinician navigates to,
         they can see at a glance that the participant is looking at something,
         and who currently holds the pen. -->
    <Teleport to="body">
        <div v-if="session.isSharing && session.sharedContent.kind !== 'form'" class="bn-th-banner d-flex align-center px-4" :style="{ right: boardRight + 'px' }" data-cy="thSharingBanner">
            <font-awesome-icon :icon="['far', 'share-nodes']" class="mr-2" />
            <span class="text-body-2">
                Sharing <strong>{{ session.sharedContent.title }}</strong>
            </span>
            <v-spacer />
            <v-btn v-if="session.sharedContent.kind === 'whiteboard'" size="small" variant="outlined" class="mr-2 text-white" data-cy="thOpenWhiteboard" @click="whiteboardOpen = true">
                <font-awesome-icon :icon="['far', 'chalkboard']" class="mr-1" />Open board
            </v-btn>
            <v-btn size="small" variant="outlined" class="text-white" data-cy="thStopSharing" @click="stopSharing">Stop sharing</v-btn>
        </div>
    </Teleport>

    <!-- A shared screen goes in the full-window surface, never the tile grid:
         at panel width a desktop renders around a tenth of its real size, and
         the grid's 4:3 tiles crop with object-fit: cover, silently cutting off
         both edges of a 16:9 capture. -->
    <Teleport to="body">
        <div v-if="session.screenShare && screenSurfaceOpen" class="bn-th-board" :style="{ right: boardRight + 'px' }" data-cy="thScreenSurface">
            <div class="bn-th-board-bar d-flex align-center px-4">
                <font-awesome-icon :icon="['far', 'display']" class="mr-2" />
                <span class="text-subtitle-2">{{ session.screenShare.local ? 'You are sharing your screen' : `${session.screenShare.name} is sharing their screen` }}</span>
                <v-spacer />
                <v-btn v-if="session.screenShare.local" size="small" variant="outlined" color="error" class="mr-2" data-cy="thStopScreenShare" @click="session.stopScreenShare()">Stop sharing</v-btn>
                <v-btn size="small" variant="text" data-cy="thScreenSurfaceClose" @click="screenSurfaceOpen = false"> <font-awesome-icon :icon="['far', 'xmark']" class="mr-1" />Close </v-btn>
            </div>
            <div class="bn-th-board-surface">
                <!-- contain, not cover: a screen must never be cropped. -->
                <video v-daily-track="session.screenShare.track" autoplay playsinline muted class="bn-th-screen-video" data-cy="thScreenVideo"></video>
            </div>
        </div>
    </Teleport>

    <!-- Host's whiteboard: a full-window surface stopping at the dock, so there
         is real room to draw and the roster stays visible beside it. Teleported
         to the body because a fixed panel inside the drawer would be clipped by
         it. Closing the board does not stop sharing. -->
    <Teleport to="body">
        <!-- v-show, not v-if: the strokes live in the child component and nowhere
             else, so unmounting it on Close would silently discard the drawing
             the patient is still looking at. -->
        <div v-if="session.isSharing" v-show="whiteboardOpen" class="bn-th-board" :style="{ right: boardRight + 'px' }" data-cy="thBoardSurface">
            <div class="bn-th-board-bar d-flex align-center px-4">
                <font-awesome-icon :icon="['far', 'chalkboard']" class="mr-2" />
                <span class="text-subtitle-2">Whiteboard</span>
                <span class="text-caption text-medium-emphasis ml-3">Not saved — closing or ending the session discards it</span>
                <v-spacer />
                <v-btn size="small" variant="text" data-cy="thWhiteboardClose" @click="whiteboardOpen = false"> <font-awesome-icon :icon="['far', 'xmark']" class="mr-1" />Close </v-btn>
            </div>
            <div class="bn-th-board-surface">
                <BnTelehealthWhiteboard
                    ref="whiteboard"
                    can-draw
                    :height="boardSurfaceHeight"
                    @stroke="session.sendAppMessage({ t: 'wb', stroke: $event })"
                    @clear="session.sendAppMessage({ t: 'wb-clear' })"
                />
            </div>
        </div>
    </Teleport>

    <!-- Add participant dialog (two-step: search → invite). Kept OUTSIDE the dark
         drawer so it renders in the app's normal (light) theme, not the dock's dark one. -->
    <v-dialog v-model="session.addOpen" max-width="460" persistent :z-index="2600">
        <v-card>
            <!-- Same title treatment as every other dialog (BnBaseDialog). Not
                 v-card-title.text-h6: this project's type scale maps text-h6 to
                 0.7rem, which renders a dialog title at caption size. -->
            <v-toolbar density="compact" flat>
                <v-toolbar-title>Add participant</v-toolbar-title>
            </v-toolbar>
            <v-divider />
            <template v-if="session.addStage === 'search'">
                <v-card-text>
                    <BnContactSearch label="Search contacts" :search-resources="['patient', 'person']" no-add autofocus data-cy="thAddSearch" @item-clicked="session.addContact" />
                    <div class="text-caption mt-2 text-medium-emphasis">They'll get their own join link and appear in the roster.</div>
                    <div v-if="session.addBusy" class="d-flex align-center mt-3"><v-progress-circular indeterminate size="18" width="2" color="primary" class="mr-2" />Adding…</div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn variant="text" data-cy="thAddCancel" :disabled="session.addBusy" @click="session.addOpen = false">Cancel</v-btn>
                </v-card-actions>
            </template>
            <template v-else>
                <v-card-text>
                    <div class="text-body-1 mb-3" data-cy="thAddInvitePrompt">
                        Send <strong>{{ session.addedContact.displayName }}</strong> their link to join?
                    </div>
                    <div class="d-flex flex-wrap ga-2">
                        <v-btn v-if="session.addedContact.sms" color="primary" variant="tonal" data-cy="thAddInviteSms" @click="session.inviteAdded('sms')">
                            <font-awesome-icon :icon="['far', 'comment-sms']" class="mr-1" />Text
                        </v-btn>
                        <v-btn v-if="session.addedContact.email" color="primary" variant="tonal" data-cy="thAddInviteEmail" @click="session.inviteAdded('email')">
                            <font-awesome-icon :icon="['far', 'envelope']" class="mr-1" />Email
                        </v-btn>
                        <v-btn variant="text" data-cy="thAddInviteSkip" @click="session.inviteAdded(null)">
                            {{ session.addedContact.sms || session.addedContact.email ? 'Skip' : 'Done' }}
                        </v-btn>
                    </div>
                    <div v-if="!session.addedContact.sms && !session.addedContact.email" class="text-caption text-medium-emphasis mt-3">
                        No consented text number or email on file — share the link manually if needed.
                    </div>
                </v-card-text>
            </template>
        </v-card>
    </v-dialog>
</template>

<style scoped>
/* The drawer is positioned by Vuetify's layout (location=right). The collapsed rail
   reserves its 72px column and reflows v-main; the docked/full states reserve 0 (see
   dockWidth) and float OVER content. We style the content as a full-height dark
   flex column. */
.bn-th-dock {
    color: #fff;
    /* Above Vuetify's overlay layer (2400): the form filler is a dialog, and at
       the drawer's default depth the dock sat underneath it — visible only
       because the scrim is inset, but not clickable. The call must stay usable
       while documenting. */
    z-index: 2500;
}
.bn-th-dock :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #1e1e1e;
    overflow: hidden;
}
.bn-th-dock--collapsed :deep(.v-navigation-drawer__content) {
    align-items: center;
    padding-top: 8px;
}
/* Docked: Vuetify reserves 0 (dockWidth), so the real width is bound inline from
   panelWidth and the panel floats over content, with a shadow to lift it off the
   page. Inline rather than a class because the clinician sets it by dragging. */
.bn-th-dock--docked {
    box-shadow: -6px 0 20px rgba(0, 0, 0, 0.35);
}
/* No transition mid-drag, or the edge lags the pointer. */
.bn-th-dock--resizing {
    transition: none !important;
}
.bn-th-resize {
    position: absolute;
    top: 0;
    left: 0;
    width: 10px;
    height: 100%;
    cursor: ew-resize;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    padding: 0;
    color: rgba(255, 255, 255, 0.35);
    z-index: 2;
}
.bn-th-resize:hover,
.bn-th-resize:focus-visible {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.85);
    outline: none;
}
.bn-th-resize-grip {
    width: 2px;
    height: 26px;
    border-radius: 1px;
    background: currentColor;
    box-shadow: 3px 0 0 currentColor;
}
/* Full-screen: break the drawer out of the layout to cover the whole viewport. */
.bn-th-dock--full {
    position: fixed !important;
    inset: 0 !important;
    width: 100vw !important;
    max-width: 100vw !important;
    height: 100vh !important;
    transform: none !important;
    z-index: 2147483647 !important;
}
.bn-th-rail-btn {
    color: #fff;
}
.bn-th-rail-avatars {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 12px;
    overflow-y: auto;
}
.bn-th-rail-controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 12px;
}
.bn-th-header {
    height: 44px;
    flex: 0 0 44px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.bn-th-video {
    background: #121212;
    overflow: auto;
    min-height: 0;
}
.bn-th-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 4px;
    padding: 4px;
}
.bn-th-dock--full .bn-th-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}
.bn-th-tile {
    position: relative;
    aspect-ratio: 4 / 3;
    background: #000;
    border-radius: 6px;
    overflow: hidden;
}
.bn-th-video-el {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.bn-th-video-off {
    visibility: hidden;
}
.bn-th-video-placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.bn-th-tile-label {
    position: absolute;
    left: 6px;
    bottom: 6px;
    font-size: 0.875rem;
    background: rgba(0, 0, 0, 0.55);
    padding: 1px 6px;
    border-radius: 4px;
}
.bn-th-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 160px;
    opacity: 0.6;
    font-size: 0.875rem;
}
.bn-th-roster {
    flex: 0 0 auto;
    max-height: 38vh;
    overflow-y: auto;
    background: #262626;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.bn-th-roster :deep(.v-list) {
    background: transparent;
    color: #fff;
}
.bn-th-controls {
    flex: 0 0 auto;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    background: #1a1a1a;
}
</style>

<!-- Global (un-scoped): the banner is teleported to the body, so a scoped rule
     would never reach it. Fixed to the bottom rather than the top so it can't
     collide with the app bar or the dock's own chrome. -->
<style>
/* `right` is set inline from the dock's width: a full-width bar would sit on top
   of the dock's own control row, hiding mic/camera/end. */
.bn-th-banner {
    position: fixed;
    left: 0;
    bottom: 0;
    height: 48px;
    /* Above the board and the overlay layer: it is how the clinician reopens a
       closed board, so a dialog must not bury it. */
    z-index: 2410;
    background: #3f51b5;
    color: #fff;
}
.bn-th-board {
    position: fixed;
    top: 0;
    left: 0;
    /* Stops short of the floor by the banner's 48px, so the banner stays readable
       beneath it. Above Vuetify's overlay layer (2000, +10 per stacked overlay)
       for the same reason the dock is: the form filler is a dialog, and a board
       below it opened invisibly behind the filler the clinician shared it from.
       Below the dock, which stays reachable over everything. */
    bottom: 48px;
    z-index: 2400;
    display: flex;
    flex-direction: column;
    background: #1e1e1e;
    color: #fff;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.bn-th-board-bar {
    height: 44px;
    flex: 0 0 44px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.bn-th-board-surface {
    flex: 1 1 auto;
    min-height: 0;
    padding: 12px;
}
.bn-th-screen-video {
    width: 100%;
    height: 100%;
    /* contain: a shared screen is detail-dense and must never be cropped. */
    object-fit: contain;
    background: #000;
    border-radius: 6px;
    display: block;
}
</style>

<!-- Global (un-scoped) so it can reach Usersnap's widget, which lives outside this
     component. The visible feedback button lives in an open shadow root, but its
     <us-button> host is in the main document, so hiding the host hides the button.
     Scoped to the body flag the dock sets, so nothing persists across reloads. -->
<style>
body.bn-telehealth-active us-button {
    display: none !important;
}

/* A full-screen dialog (the form filler, above all) stops at the dock's edge
   while a session is live, so the clinician can document and see the call at
   once instead of the form burying it. */
body.bn-telehealth-active .v-dialog--fullscreen > .v-overlay__content {
    right: var(--bn-dock-width, 0px);
    left: 0;
    width: auto;
    max-width: none;
}
/* The scrim has to be inset too. Insetting only the content left the dock
   visually dimmed and — because the scrim still spanned the viewport — swallowed
   every click on it, so the call looked present but dead. */
body.bn-telehealth-active .v-dialog--fullscreen > .v-overlay__scrim {
    right: var(--bn-dock-width, 0px);
    left: 0;
    width: auto;
}
/* Toasts stay on top while a session is live. A failed share and a refused
   screen share are both reported this way, and the board now covers the window
   they would otherwise appear over. `!important` because the overlay stack
   writes its z-index inline. */
body.bn-telehealth-active .v-snackbar {
    z-index: 2700 !important;
}
</style>
